import { TeamMembersManager } from "./TeamMembersManager"
import React, { useState, useEffect } from "react"
import {Shield, LogOut, Edit, MessageSquarePlus, Mail, User, X, ArrowLeft, Lightbulb, LayoutDashboard, Inbox, GitBranch, Calendar, MessageCircleDashed as MessageCircle, HelpCircle, Image, Users, Tag} from 'lucide-react'
import { lumi } from "../lib/lumi"
import { ContactRequestsList } from "./ContactRequestsList"
import { EvolutionManager } from "./EvolutionManager"
import { EventsManager } from "./EventsManager"
import { TestimonialsManager } from "./TestimonialsManager"
import { QuizQuestionsManager } from "./QuizQuestionsManager"
import { QuizResultTextsManager } from "./QuizResultTextsManager"
import { SiteImagesManager } from "./SiteImagesManager"
import { ProblemsKeywordsManager } from "./ProblemsKeywordsManager"

interface AdminPageProps {
  onClose: () => void
}

type MenuItem = 'dashboard' | 'contacts' | 'evolutions' | 'events' | 'testimonials' | 'quiz' | 'quiz-results' | 'images' | 'team' | 'problems'

export function AdminPage({ onClose }: AdminPageProps) {
  const [user, setUser] = useState(lumi.auth.user)
  const [adminMode, setAdminMode] = useState<'editor' | 'reviewer'>(
    (localStorage.getItem('adminMode') as 'editor' | 'reviewer') || 'editor'
  )
  const [activeMenu, setActiveMenu] = useState<MenuItem>('dashboard')

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user)
    })
    return unsubscribe
  }, [])

  const toggleMode = () => {
    const newMode = adminMode === 'editor' ? 'reviewer' : 'editor'
    setAdminMode(newMode)
    localStorage.setItem('adminMode', newMode)
    window.dispatchEvent(new CustomEvent('adminModeChange', { detail: newMode }))
  }

  if (!user) {
    return null
  }

  const menuItems = [
    { id: 'dashboard' as MenuItem, label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'contacts' as MenuItem, label: 'Demandes de contact', icon: Inbox },
    { id: 'team' as MenuItem, label: 'Équipe', icon: Users },
    { id: 'problems' as MenuItem, label: 'Mots-clés Problèmes', icon: Tag },
    { id: 'quiz' as MenuItem, label: 'Questions du Quiz', icon: HelpCircle },
    { id: 'quiz-results' as MenuItem, label: 'Textes Résultats Quiz', icon: MessageCircle },
    { id: 'images' as MenuItem, label: 'Images du Site', icon: Image },
    { id: 'evolutions' as MenuItem, label: 'Évolutions', icon: GitBranch },
    { id: 'events' as MenuItem, label: 'Événements', icon: Calendar },
    { id: 'testimonials' as MenuItem, label: 'Témoignages', icon: MessageCircle }
  ]

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-4 min-h-[600px] flex">
        {/* Sidebar Menu */}
        <div className="w-64 bg-gradient-to-b from-teal-600 to-cyan-600 rounded-l-2xl p-6 flex flex-col">
          {/* User Header */}
          <div className="mb-8">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              {user.userRole === "ADMIN" ? (
                <Shield className="w-8 h-8 text-teal-600" />
              ) : (
                <User className="w-8 h-8 text-teal-600" />
              )}
            </div>
            <h2 className="text-white font-bold text-center text-lg">{user.userName}</h2>
            <p className="text-teal-100 text-center text-sm">{user.userRole === "ADMIN" ? "Administrateur" : "Membre"}</p>
          </div>

          {/* Menu Items */}
          <nav className="flex-1 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveMenu(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                    activeMenu === item.id
                      ? 'bg-white text-teal-600 shadow-lg'
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </button>
              )
            })}
          </nav>

          {/* Bottom Actions */}
          <div className="space-y-2 mt-8">
            <button
              onClick={onClose}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium bg-white/20 text-white hover:bg-white/30 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour au site
            </button>
            <button
              onClick={() => {
                lumi.auth.signOut()
                onClose()
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-8 overflow-y-auto">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>

          {/* Dashboard View */}
          {activeMenu === 'dashboard' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Tableau de bord</h1>
              
              {/* User Info Card */}
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-xl mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Informations du compte</h2>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-teal-600" />
                    <span className="text-gray-700"><strong>Nom :</strong> {user.userName}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-teal-600" />
                    <span className="text-gray-700"><strong>Email :</strong> {user.userEmail}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-teal-600" />
                    <span className="text-gray-700"><strong>Rôle :</strong> {user.userRole === "ADMIN" ? "Administrateur" : "Membre"}</span>
                  </div>
                </div>
              </div>

              {/* Admin Controls (only for admins) */}
              {user.userRole === "ADMIN" && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Outils d'administration</h2>
                  
                  {/* Mode Switcher */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Mode de modification</h3>
                    <button
                      onClick={toggleMode}
                      className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all text-lg ${
                        adminMode === 'editor'
                          ? "bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg"
                          : "bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg"
                      }`}
                    >
                      {adminMode === 'editor' ? (
                        <>
                          <Edit className="w-5 h-5" />
                          Mode Éditeur Actif
                        </>
                      ) : (
                        <>
                          <MessageSquarePlus className="w-5 h-5" />
                          Mode Relecteur Actif
                        </>
                      )}
                    </button>
                    
                    <div className="mt-4 p-4 bg-teal-50 rounded-lg">
                      <p className="text-sm text-gray-700">
                        {adminMode === 'editor' 
                          ? '✏️ Survolez les éléments de la page pour les modifier directement'
                          : '💬 Survolez les éléments de la page pour ajouter des commentaires et suggestions'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Requests View */}
          {activeMenu === 'contacts' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Demandes de contact</h1>
              <ContactRequestsList onClose={() => {}} embedded />
            </div>
          )}

          {/* Team Members View */}
          {activeMenu === 'team' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Gestion de l'équipe</h1>
              <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
                <TeamMembersManager onClose={() => setActiveMenu('dashboard')} />
              </div>
            </div>
          )}

          {/* Evolutions View */}
          {activeMenu === 'evolutions' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Gestion des évolutions</h1>
              <EvolutionManager onClose={() => {}} embedded />
            </div>
          )}

          {/* Events View */}
          {activeMenu === 'events' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Gestion des événements</h1>
              <EventsManager onClose={() => {}} embedded />
            </div>
          )}

          {/* Quiz Questions View */}
          {activeMenu === 'quiz' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Gestion des Questions du Quiz</h1>
              <QuizQuestionsManager onClose={() => {}} embedded />
            </div>
          )}

          {/* Quiz Result Texts View */}
          {activeMenu === 'quiz-results' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Gestion des Textes de Résultats</h1>
              <QuizResultTextsManager onClose={() => {}} embedded />
            </div>
          )}

          {/* Images View */}
          {activeMenu === 'images' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Gestion des Images du Site</h1>
              <SiteImagesManager onClose={() => {}} embedded />
            </div>
          )}

          {/* Problems Keywords View */}
          {activeMenu === 'problems' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Gestion des Mots-clés Problèmes</h1>
              <ProblemsKeywordsManager onClose={() => {}} embedded />
            </div>
          )}

          {/* Testimonials View */}
          {activeMenu === 'testimonials' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Gestion des témoignages</h1>
              <TestimonialsManager onClose={() => {}} embedded />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
